import React, { useState, useEffect, useRef } from 'react';

import { TextField, TextFieldProps, InputAdornment, Icon, Button } from '@mui/material';
import { Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { useTheme } from '@mui/material/styles';
import { RiSearchLine } from 'react-icons/ri';
import Spinner from './Spinner';
import { TFunction } from 'i18next';
import useDebouncedEffect from 'common/hooks/useDebouncedEffect';

/** Debounced uncontrolled search input for those situations where every re-render matters. */

interface Props {
	onChange?: (value: string) => void;
	className?: string;
	delay?: number;
	textFieldProps?: TextFieldProps;
	t: TFunction;
	searchValue?: string;
}

const SearchInput = ({
	className,
	textFieldProps,
	onChange,
	delay = 300,
	searchValue,
	t,
}: Props) => {
	const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
	const classes = useStyles();
	const theme = useTheme();
	const [isDebouncing, setIsDebouncing] = useState<boolean>(false);
	const [value, setValue] = useState<string>(searchValue ?? '');

	useEffect(() => {
		if (searchValue && searchValue !== value) {
			setValue(searchValue);
		}
	}, [searchValue]);

	useEffect(() => {
		if (searchValue && searchValue !== value) {
			setValue(searchValue);
		}
	}, [searchValue]);

	useDebouncedEffect(
		() => {
			if (value !== searchValue) {
				onChange?.(value);
			}
			setIsDebouncing(false);
		},
		delay,
		[value, searchValue, onChange],
	);

	const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		setValue(e.target.value);
		setIsDebouncing(true);
	};

	const handleReset = () => {
		setValue('');
		inputRef?.current?.focus();
		if (onChange) {
			onChange('');
		}
	};

	return (
		<TextField
			inputRef={inputRef}
			className={className}
			variant="outlined"
			fullWidth
			{...textFieldProps}
			value={value}
			onChange={handleChange}
			InputProps={{
				...textFieldProps?.InputProps,
				startAdornment: (
					<InputAdornment position="start">
						<Icon className={classes.iconWrapper}>
							{isDebouncing && delay > 0 ? (
								<Spinner size={16} />
							) : (
								<RiSearchLine size={18} color={theme.palette.text.secondary} />
							)}
						</Icon>
					</InputAdornment>
				),
				endAdornment: !!value && (
					<Button size="small" className={classes.clearButton} onClick={handleReset}>
						{t('common:actions.clear', 'Clear')}
					</Button>
				),
			}}
		/>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		iconWrapper: {
			height: theme.spacing(3),
			width: theme.spacing(3),
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			position: 'relative',
		},
		clearButton: {
			borderRadius: 0,
			maxHeight: '32px',
			minHeight: '32px',
			minWidth: 0,
			padding: theme.spacing(1, 2),
			marginRight: -theme.spacing(1),
		},
	}),
);

export default SearchInput;
